import { useId, useState } from 'react';
import type { FieldMetadata, FieldValue } from '../../../data/client';
import { Button, Input, Popover, useToast } from '../../../ui';
import { todayKey } from '../../record/dueDate';
import { useRecordFieldSave } from '../../record/useRecordFieldSave';
import { pausedToastTitle, pausedUntilWords } from './paused';

/**
 * PAUSE A CLIENT UNTIL A DATE (redesign register A13, D6; record 11, ledger RD-HEALTH-1; brief
 * FE-FOUNDATION section 13).
 *
 * A13: the pause sits "on the Dashboard row next to "Mark as handled"; pausing ahead of time
 * uses the "Paused until" field in Edit facts on the client page, no second button."
 *
 * `PauseAction` is the row's text action "Pause…" (open question Q-FND-10, decided: "Pause…"
 * and "Pause"). It opens a popover - menu glass, L9 - with one date field labelled by the
 * field's own label ("Paused until", read from metadata, never retyped), Cancel and Pause
 * (components page section 4, variant B). No note: no field exists for one (Q-FND-11). The write
 * is the record's ordinary field save (`useRecordFieldSave`), so a refusal or a queued approval
 * says what it always says. On success the page hides the client at once (`onPaused`) and the
 * toast offers Undo, which empties `paused_until` again (Q-FND-12, decided: "<Client> paused
 * until <date>" with Undo).
 *
 * `PausedUntilLine` is how a paused client reads where it is shown (Clients today, the client
 * page tomorrow): "Paused until 12 October 2026" and "Resume", which empties the date.
 */
export interface PauseActionProps {
  readonly agencyObjectId: string;
  readonly agency: { readonly id: string; readonly name: string };
  /** `agency.paused_until`; the action is not drawn without it. */
  readonly field: FieldMetadata | undefined;
  /** The page hides the client at once. */
  readonly onPaused?: ((agencyId: string) => void) | undefined;
  /** Undo brought the client back. */
  readonly onResumed?: ((agencyId: string) => void) | undefined;
  /** The value the field holds now, which Undo writes back. */
  readonly current?: FieldValue | undefined;
}

export function PauseAction({
  agencyObjectId,
  agency,
  field,
  onPaused,
  onResumed,
  current = null,
}: PauseActionProps) {
  const toast = useToast();
  const { save, savingFieldId } = useRecordFieldSave(agencyObjectId, agency.id);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const inputId = useId();
  if (field === undefined) return null;
  const today = todayKey();

  const confirm = async () => {
    if (day === '') return;
    const outcome = await save(field, day);
    if (outcome !== 'applied') return;
    setOpen(false);
    onPaused?.(agency.id);
    toast({
      title: pausedToastTitle(agency.name, day),
      tone: 'success',
      action: {
        label: 'Undo',
        altText: `Undo the pause for ${agency.name}`,
        onAction: () => {
          void save(field, current).then((undone) => {
            if (undone === 'applied') onResumed?.(agency.id);
          });
        },
      },
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDay('');
      }}
      align="end"
      ariaLabel={agency.name}
      trigger={
        <Button variant="text" data-pause-action="">
          Pause…
        </Button>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <label htmlFor={inputId} className="block text-2xs text-ink-muted">
          {field.label}
        </label>
        <Input
          id={inputId}
          type="date"
          min={today}
          value={day}
          onChange={(event) => setDay(event.target.value)}
          data-pause-date=""
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="prominent"
            type="submit"
            disabled={day === '' || day < today}
            loading={savingFieldId === field.id}
          >
            Pause
          </Button>
        </div>
      </form>
    </Popover>
  );
}

export interface PausedUntilLineProps {
  readonly agencyObjectId: string;
  readonly agencyId: string;
  /** `agency.paused_until`. */
  readonly field: FieldMetadata;
  /** The stored date, `YYYY-MM-DD`. */
  readonly pausedUntil: string;
  readonly onResumed?: ((agencyId: string) => void) | undefined;
  readonly className?: string | undefined;
}

export function PausedUntilLine({
  agencyObjectId,
  agencyId,
  field,
  pausedUntil,
  onResumed,
  className,
}: PausedUntilLineProps) {
  const { save, savingFieldId } = useRecordFieldSave(agencyObjectId, agencyId);
  return (
    <span className={className} data-paused-until={pausedUntil}>
      <span className="text-ink-muted">{pausedUntilWords(pausedUntil)}</span>{' '}
      <Button
        variant="text"
        loading={savingFieldId === field.id}
        onClick={() => {
          void save(field, null).then((outcome) => {
            if (outcome === 'applied') onResumed?.(agencyId);
          });
        }}
        data-resume=""
      >
        Resume
      </Button>
    </span>
  );
}
